import {
  ArrowLeftIcon,
  BuildingLibraryIcon,
  BuildingOffice2Icon,
  BuildingStorefrontIcon,
  CalendarIcon, CheckIcon, ClipboardDocumentListIcon, CurrencyEuroIcon, DocumentChartBarIcon, HashtagIcon, HomeIcon, MapPinIcon, PencilIcon, ShareIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import { BarChart, LineChart } from '@mui/x-charts';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { formatCurrency, formatDate } from '../../../../common-utils/formatters';
import type { AsyncStatus } from '../../../@types/AsyncStatus';
import type { ValuationHistoryItem } from '../../../@types/Valuation';
import { 
  setCoprosFormData,
} from '../../../store/CoproReducer';
import { useAppDispatch } from '../../../store/hook';
import type { RootState } from '../../../store/store';
import { getLabelByValue } from '../../../utils/attributions';
import { coproTypes } from '../../../utils/copros';
import { dpeClasses, getDpeColor } from '../../../utils/dpeColors';
import { JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import Badge from '../../common/Badge';
import CoproForm from '../../common/CoproForm';
import KPICard from '../../common/KPICards';
import MUITable from '../../common/MUITable';
import { showToast } from '../../common/Notification';
import Popup from '../../common/Popup/Popup.module';
import RedirectionMapButtons from '../../common/RedirectionMapButtons/RedirectionMapButtons.module';
import Spinner from '../../common/Spinner';
import type { ValuationHistoryHandle } from '../ValuationModal/ValuationHistory';
import ValuationHistory from '../ValuationModal/ValuationHistory';
import ValuationModal from '../ValuationModal/ValuationModal';
import CoproDetailsMap from './components/CoproDetailsMap';
import CoproHistory from './components/CoproHistory';

const CoproID: React.FC = () => {
  const { coproId } = useParams<{ coproId: string }>();
  const navigate = useNavigate();
  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company);

  const [copro, setCopro] = useState(null);
  const [status, setStatus] = useState<AsyncStatus>('IDLE');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [attributions, setAttributions] = useState([]);
  const [dpes, setDPEs] = useState([]);
  const [sales, setSales] = useState([]);
  const [totalSales, setTotalSales] = useState<number | null>(null);
  const [isSalesPopupOpen, setIsSalesPopupOpen] = useState(false);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [coproAddress, setCoproAddress] = useState<string>('');
  const [parcelles, setParcelles] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [sciCount, setSciCount] = useState<number | null>(null);
  const [bauxCount, setBauxCount] = useState<number | null>(null);
  const [bauxDeadlineCount, setBauxDeadlineCount] = useState<number | null>(null);
  const [dpeCountByYearAndClass, setDpeCountByYearAndClass] = useState<{ year: string; A: number; B: number; C: number; D: number; E: number; F: number; G: number }[]>([]);
  const [salesByYear, setSalesByYear] = useState<{ date: string; amount: number }[]>([]);
  const [isEditingExternalCode, setIsEditingExternalCode] = useState(false);
  const [externalBuildingCode, setExternalBuildingCode] = useState<string>('');
  const [externalBuildingCodeSaving, setExternalBuildingCodeSaving] = useState(false);

  const [valuationModalOpen, setValuationModalOpen] = useState(false);
  const [initialValuation, setInitialValuation] = useState<ValuationHistoryItem | undefined>(undefined);
  const valuationHistoryRef = useRef<ValuationHistoryHandle>(null);

  const dispatch = useAppDispatch();
  const coprosFormData = useSelector((state: RootState) => state.copro.coprosFormData);
  const [isCoproDataLoaded, setIsCoproDataLoaded] = useState(false);

  const fetchCoproDetails = async() => {
    setStatus('PENDING');
    try {
      const response = await fetch(`/api/copros/${coproId}`, JWTHeader(jwt));
      if (!response.ok) {
        throw new Error(`HTTP error status: ${response.status}`);
      }
      const data = await response.json();
      setCopro(data);
      setStatus('SUCCESS');

      // Extract parcelles and addresses from the copro data
      if (data.plots) {
        setParcelles(data.plots);
      }
      if (data.addresses) {
        setAddresses(data.addresses);
      }

      // Check if the copro is in the store, if not, add it
      if (!coprosFormData.some((coproStored) => coproStored.id === parseInt(coproId, 10))) {
        dispatch(setCoprosFormData([...coprosFormData, data]));
      }
      setIsCoproDataLoaded(true);

      // Set the full address
      if (data.addresses && data.addresses[0]) {
        const { address, postalCode, city } = data.addresses[0];
        setCoproAddress(`${address}, ${postalCode} ${city}`);
      }

      // Set external building code
      setExternalBuildingCode(data.externalBuildingCode || '');
    } catch (error) {
      console.error('Error fetching copro details :', error);
      setStatus('ERROR');
      setErrorMessage('Erreur lors du chargement des détails de la copropriété');
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
    }
  };

  const fetchAttributions = async() => {
    try {
      const response = await fetch(`/api/copros/${coproId}/attributions?limit=10`, JWTHeader(jwt));
      if (!response.ok) {
        throw new Error(`HTTP error status: ${response.status}`);
      }
      const data = await response.json();
      setAttributions(data.results);
    } catch (error) {
      console.error('Error fetching attributions :', error);
      showToast('error', 'Une erreur est survenue lors du chargement des attributions.');
    }
  };

  const fetchDPEs = async() => {
    try {
      const response = await fetch(`/api/copros/${coproId}/dpes/history`, JWTHeader(jwt));
      if (!response.ok) {
        throw new Error(`HTTP error status: ${response.status}`);
      }
      const data = await response.json();
      setDPEs(data);

      // Process DPE data for the chart
      const dpeYearClassCounts = data.reduce((acc, dpe) => {
        const year = new Date(dpe.dateReceptionDpe).getFullYear().toString();
        if (!acc[year]) {
          acc[year] = {
            A: 0, B: 0, C: 0, D: 0, E: 0, F: 0, G: 0,
          };
        }
        acc[year][dpe.classeBilanDpe]++;
        return acc;
      }, {});

      // Ensure all years from 2021 to current year are included
      const currentYear = new Date().getFullYear();
      const allYears = Array.from({ length: currentYear - 2020 }, (_, i) => (2021 + i).toString());

      const formattedData = allYears.map((year) => ({
        year,
        ...(dpeYearClassCounts[year] || {
          A: 0, B: 0, C: 0, D: 0, E: 0, F: 0, G: 0,
        }),
      }));

      setDpeCountByYearAndClass(formattedData);
    } catch (error) {
      console.error('Error fetching DPEs :', error);
      showToast('error', 'Une erreur est survenue lors du chargement des DPEs.');
    }
  };

  const fetchSales = async() => {
    try {
      const currentYear = new Date().getFullYear();
      const lastThreeYears = [currentYear - 1, currentYear - 2, currentYear - 3];
      const params = new URLSearchParams();
      lastThreeYears.forEach((year) => params.append('years[]', year.toString()));

      const response = await fetch(`/api/copros/${coproId}/stats/sales/details/years?${params.toString()}`, JWTHeader(jwt));
      if (!response.ok) {
        throw new Error(`HTTP error status: ${response.status}`);
      }
      const data = await response.json();
      setSales(data);

      // Process sales data for the chart
      const salesData = data.map((sale) => ({
        date: sale.datemut,
        amount: Number(sale.valeurfonc),
      })).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      setSalesByYear(salesData);

      // Calculate total sales
      const total = data.reduce((sum, sale) => sum + Number(sale.valeurfonc), 0);
      setTotalSales(total);
    } catch (error) {
      console.error('Error fetching sales :', error);
      showToast('error', 'Une erreur est survenue lors du chargement des ventes.');
    }
  };

  const fetchSCICount = async() => {
    try {
      const response = await fetch(`/api/copros/${coproId}/scis`, JWTHeader(jwt));
      if (!response.ok) {
        throw new Error(`HTTP error status: ${response.status}`);
      }
      const data = await response.json();
      setSciCount(data.length);
    } catch (error) {
      console.error('Error fetching SCI count :', error);
      showToast('error', 'Une erreur est survenue lors du chargement du nombre de SCIs.');
    }
  };

  const fetchBaux = async() => {
    try {
      if (coproId && jwt) {
        const response = await fetch(`/api/copros/${coproId}/baux`, JWTHeader(jwt));
        if (!response.ok) {
          throw new Error(`HTTP error status: ${response.status}`);
        }
        const bauxData = await response.json();
        setBauxCount(bauxData.length);
        
        const responseDeadline = await fetch(`/api/copros/${coproId}/baux?months=6`, JWTHeader(jwt));
        if (!responseDeadline.ok) {
          throw new Error(`HTTP error status: ${responseDeadline.status}`);
        }
        const bauxDeadlineData = await responseDeadline.json();
        setBauxDeadlineCount(bauxDeadlineData.length);
      }
    } catch (error) {
      console.error('Error fetching baux :', error);
      showToast('error', 'Une erreur est survenue lors du chargement des baux commerciaux.');
    }
  };

  useEffect(() => {
    if (coproId && jwt) {
      fetchCoproDetails();
      fetchAttributions();
      fetchDPEs();
      fetchSales();
      fetchSCICount();
      fetchBaux();
    }
  }, [coproId, jwt, coprosFormData]);

  const handleEditCopro = () => {
    setIsEditPopupOpen(true);
  };

  const handleCopyLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      showToast('success', 'Lien de la copropriété copié dans le presse-papiers.');
    }).catch((err) => {
      console.error('Failed to copy: ', err);
      showToast('error', 'Échec de la copie du lien de la copropriété.');
    });
  };

  const handleCopyRemoteId = () => {
    if (copro.remoteId) {
      navigator.clipboard.writeText(copro.remoteId).then(() => {
        showToast('success', 'Numéro d\'immatriculation copié dans le presse-papiers.');
      }).catch((err) => {
        console.error('Failed to copy: ', err);
        showToast('error', 'Échec de la copie du numéro d\'immatriculation.');
      });
    }
  };

  const attributionHeaders = [
    { title: 'RÔLE', class: 'w-1/4' },
    { title: 'NOM', class: 'w-1/4' },
    { title: 'EMAIL', class: 'w-1/4' },
  ];

  const attributionData = attributions.map((attribution) => ({
    RÔLE: getLabelByValue(attribution.fonctionType),
    NOM: `${attribution.user.lastname.toUpperCase()} ${attribution.user.firstname}`,
    EMAIL: attribution.user.email,
  }));

  const handleViewDPEs = () => {
    navigate(ROUTES.PROSPECTING_DPE_VISION, { state: { coproId: parseInt(coproId, 10) } });
  };

  const handleViewSales = () => {
    setIsSalesPopupOpen(true);
  };

  const handleViewSCIs = () => {
    navigate(ROUTES.MONITORING_SCIS, { state: { coproId: parseInt(coproId, 10) } });
  };

  const handleViewBaux = () => {
    navigate(ROUTES.MONITORING_BAUX_COMMERCIAUX_COPRO.replace(':coproId', coproId));
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleSaveExternalBuildingCode = async() => {
    setExternalBuildingCodeSaving(true);
    try {
      const response = await fetch(`/api/copros/${coproId}/external-building-code`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...JWTHeader(jwt).headers,
        },
        body: JSON.stringify({ externalBuildingCode: externalBuildingCode.trim() || null }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error status: ${response.status}`);
      }
      showToast('success', 'Code immeuble mis à jour.');
      setIsEditingExternalCode(false);
      // Update copro state
      setCopro((prev) => prev ? { ...prev, externalBuildingCode: externalBuildingCode.trim() || null } : prev);
    } catch (error) {
      console.error('Error updating external building code:', error);
      showToast('error', 'Erreur lors de la mise à jour du code immeuble.');
    }
    setExternalBuildingCodeSaving(false);
  };

  const handleCancelEditExternalCode = () => {
    setExternalBuildingCode(copro?.externalBuildingCode || '');
    setIsEditingExternalCode(false);
  };

  return (
    <div className="flex flex-col space-y-8">
      <div className="rounded-lg bg-white p-6 shadow-md">
        {status === 'PENDING' && (
          <div className="flex h-64 items-center justify-center">
            <Spinner aria-label="Loading copro details" size="xl" />
          </div>
        )}

        {status === 'ERROR' && (
          <div className="mx-10 my-32 text-center">
            <Badge mode="error" text={errorMessage} />
          </div>
        )}

        {status === 'SUCCESS' && copro && (
          <>
            <div className="mb-6 items-center justify-between md:flex">
              <div className="flex items-center">
                <button
                  onClick={handleGoBack}
                  className="mr-4 text-rgpd-blue transition-colors hover:text-rgpd-blue-dark"
                >
                  <ArrowLeftIcon className="h-6 w-6" />
                </button>
                <div>
                  <h1 className="flex items-center text-3xl font-bold text-rgpd-blue-dark">
                    <BuildingOffice2Icon className="mr-3 h-10 w-10" />
                    {copro.name}
                  </h1>
                  {copro.remoteId && (
                    <p className="mt-2 mb-2 ml-0 flex items-center text-sm text-gray-500 md:mt-1 md:mb-0 md:ml-14">
                      Numéro d'immat. :
                      {' '}
                      {copro.remoteId}
                      <button
                        onClick={handleCopyRemoteId}
                        className="ml-2 rounded-full border border-gray-500 p-0.5 text-gray-500 hover:bg-gray-500 hover:text-white"
                        title="Copier le numéro d'immatriculation"
                      >
                        <ClipboardDocumentListIcon className="h-4 w-4" />
                      </button>
                    </p>
                  )}
                </div>
              </div>
              <div className="md:flex md:justify-end md:space-x-3 lg:space-x-5">
                <button
                  onClick={handleCopyLink}
                  className="text-rgpd-blue transition-colors hover:text-rgpd-blue-dark"
                  title="Copier le lien"
                >
                  <ShareIcon className="h-6 w-6" />
                </button>
                <button
                  onClick={handleEditCopro}
                  className={`mt-2 flex w-full items-center justify-center rounded bg-rgpd-blue px-4 py-2 text-white transition-colors hover:bg-rgpd-blue-dark md:mt-0 md:w-auto ${!isCoproDataLoaded ? 'cursor-not-allowed opacity-50' : ''}`}
                  disabled={!isCoproDataLoaded}
                >
                  <PencilIcon className="mr-2 h-5 w-5" />
                  Modifier
                </button>
              </div>
            </div>
            <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
              <KPICard
                icon={<MapPinIcon className="h-6 w-6" />}
                title="Adresse"
                value={coproAddress || 'Non renseignée'}
              />
              <KPICard
                icon={<MapPinIcon className="h-6 w-6" />}
                title="Voir sur carte"
              >
                {coproAddress && (
                  <RedirectionMapButtons address={coproAddress} type="standard-inline" />
                )}
                {
                  !coproAddress && (
                    <div className="flex items-center justify-center text-lg font-semibold">Non renseignée</div>
                  )
                }
              </KPICard>
              <KPICard
                icon={<BuildingOffice2Icon className="h-6 w-6" />}
                title="Type"
                value={coproTypes[copro.type]}
              />
              <KPICard
                icon={<HashtagIcon className="h-6 w-6" />}
                title="Code immeuble (logiciel métier)"
              >
                {isEditingExternalCode ? (
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={externalBuildingCode}
                      onChange={(e) => setExternalBuildingCode(e.target.value)}
                      className="w-full rounded border px-2 py-1 text-sm"
                      placeholder="Code immeuble..."
                      autoFocus
                    />
                    <button
                      onClick={handleSaveExternalBuildingCode}
                      disabled={externalBuildingCodeSaving}
                      className="text-green-600 hover:text-green-800 disabled:opacity-50"
                      title="Enregistrer"
                    >
                      <CheckIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={handleCancelEditExternalCode}
                      className="text-red-600 hover:text-red-800"
                      title="Annuler"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold">
                      {copro.externalBuildingCode || 'Non renseigné'}
                    </span>
                    <button
                      onClick={() => setIsEditingExternalCode(true)}
                      className="ml-2 text-rgpd-blue hover:text-rgpd-blue-dark"
                      title="Modifier"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </KPICard>
            </div>

            <h2 className="mb-4 text-2xl font-semibold">Agenda de la copropriété</h2>
            <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              <KPICard
                icon={<CalendarIcon className="h-6 w-6" />}
                title="Début de mandat"
                value={copro.termStartAt ? formatDate(copro.termStartAt) : 'Non renseignée'}
              />
              <KPICard
                icon={<CalendarIcon className="h-6 w-6" />}
                title="Fin de mandat"
                value={copro.termEndAt ? formatDate(copro.termEndAt) : 'Non renseignée'}
              />
              <KPICard
                icon={<CalendarIcon className="h-6 w-6" />}
                title="Date d'assemblée générale"
                value={copro.generalMeetingDate ? formatDate(copro.generalMeetingDate) : 'Non enregistrée'}
                subtitles={[
                  { label: 'Valeur dans le registre', value: copro.generalMeetingApiDate ? formatDate(copro.generalMeetingApiDate) : 'Non renseignée' },
                ]}
              />
            </div>

            <h2 className="mb-4 text-2xl font-semibold">Chiffres clés</h2>
            <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
              <KPICard
                icon={<HomeIcon className="h-6 w-6" />}
                title="Nombre de lots habitable"
                value={copro.batchSize}
              />
              <KPICard
                icon={<DocumentChartBarIcon className="h-6 w-6" />}
                title="Nombre de DPEs depuis 2021"
                value={dpes.length}
                onClick={handleViewDPEs}
              />
              <KPICard
                icon={<CurrencyEuroIcon className="h-6 w-6" />}
                title="Ventes (3 dernières années)"
                value={totalSales ? formatCurrency(totalSales) : '0€'}
                loading={totalSales === null} // keep loading if 0 otherwise
                onClick={handleViewSales}
              />
              <KPICard
                icon={<BuildingLibraryIcon className="h-6 w-6" />}
                title="Nombre de SCIs"
                value={sciCount !== null ? sciCount.toString() : 'Chargement...'}
                loading={sciCount === null}
                onClick={handleViewSCIs}
              />
            </div>
            <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
              <KPICard
                icon={<BuildingStorefrontIcon className="h-6 w-6" />}
                title="Entreprises / Commerces / Pieds d'immeuble"
                value={bauxCount !== null ? bauxCount.toString() : 'Chargement...'}
                loading={bauxCount === null}
                subtitles={[
                  { label: 'À échéance (6 mois)', value: bauxDeadlineCount !== null ? bauxDeadlineCount.toString() : '...' },
                ]}
                onClick={handleViewBaux}
              />
            </div>
            <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <h3 className="mb-4 text-xl font-semibold">DPEs</h3>
                {dpeCountByYearAndClass.length > 0 && dpeCountByYearAndClass.some((item) => item.A > 0 || item.B > 0 || item.C > 0 || item.D > 0 || item.E > 0 || item.F > 0 || item.G > 0) ? (
                  <BarChart
                    xAxis={[{
                      scaleType: 'band',
                      data: dpeCountByYearAndClass.map((item) => item.year),
                    }]}
                    series={dpeClasses.map((dpeClass) => ({
                      data: dpeCountByYearAndClass.map((item) => item[dpeClass]),
                      label: dpeClass,
                      color: getDpeColor(dpeClass),
                    }))}
                    height={300}
                  />
                ) : (
                  <div className="flex h-[300px] items-center justify-center">
                    <Badge mode="warning" text="Aucune donnée" />
                  </div>
                )}
              </div>
              <div>
                <h3 className="mb-4 text-xl font-semibold">Ventes (3 dernières années)</h3>
                {salesByYear.length > 0 ? (
                  <LineChart
                    xAxis={[{
                      data: salesByYear.map((item) => new Date(item.date).getTime()),
                      scaleType: 'time',
                      valueFormatter: (value) => formatDate(value),
                    }]}
                    series={[
                      {
                        data: salesByYear.map((item) => item.amount),
                        area: true,
                        color: 'rgb(59, 130, 246)',
                        valueFormatter: (value) => formatCurrency(value),
                      },
                    ]}
                    height={300}
                    margin={{
                      left: 70, right: 20, top: 20, bottom: 30,
                    }}
                    slotProps={{
                      legend: {
                        hidden: true,
                      },
                    }}
                    tooltip={{
                      axisContent: (params) => {
                        if (params && params.dataIndex !== undefined) {
                          const dataPoint = salesByYear[params.dataIndex];
                          const date = formatDate(dataPoint.date);
                          const value = formatCurrency(dataPoint.amount);
                          return (
                            <div className="rounded-lg border-2 border-rgpd-blue bg-rgpd-white p-2">
                              <div>
                                Date :
                                {date}
                              </div>
                              <div>
                                Montant :
                                {value}
                              </div>
                            </div>
                          );
                        }
                        return null;
                      },
                    }}
                  />
                ) : (
                  <div className="flex h-[300px] items-center justify-center">
                    <Badge mode="warning" text="Aucune donnée" />
                  </div>
                )}
              </div>
            </div>

            <h2 className="mb-4 text-2xl font-semibold">Générer un rapport</h2>
            <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-4">
              <button
                onClick={() => navigate(ROUTES.PROSPECTING_GENERATE_DPE_REPORT, { state: { selectedCoproId: parseInt(coproId, 10) } })}
                className="flex items-center justify-center rounded bg-rgpd-blue px-4 py-2 text-white transition-colors hover:bg-rgpd-blue-dark"
              >
                <DocumentChartBarIcon className="mr-2 h-5 w-5" />
                Rapport des DPEs
              </button>
              <button
                onClick={() => navigate(ROUTES.ANALYZING_GENERATE_SALES_REPORT, { state: { selectedCoproId: parseInt(coproId, 10) } })}
                className="flex items-center justify-center rounded bg-rgpd-blue px-4 py-2 text-white transition-colors hover:bg-rgpd-blue-dark"
              >
                <CurrencyEuroIcon className="mr-2 h-5 w-5" />
                Rapport des ventes
              </button>
              <button
                onClick={() => navigate(ROUTES.ANALYZING_GENERATE_COPRO_REPORT, { state: { selectedCoproId: parseInt(coproId, 10) } })}
                className="flex items-center justify-center rounded bg-rgpd-blue px-4 py-2 text-white transition-colors hover:bg-rgpd-blue-dark"
              >
                <BuildingOffice2Icon className="mr-2 h-5 w-5" />
                Rapport de copropriété
              </button>
              <button
                onClick={() => { setInitialValuation(undefined); setValuationModalOpen(true); }}
                className="flex items-center justify-center rounded bg-red-600 px-4 py-2 text-white transition-colors hover:bg-red-700"
              >
                <HomeIcon className="mr-2 h-5 w-5" />
                Avis de valeur
              </button>
            </div>

            {company && (
              <div className="mb-8">
                <h2 className="mb-4 text-2xl font-semibold">Historique des avis de valeur</h2>
                <ValuationHistory
                  ref={valuationHistoryRef}
                  companyId={company.id}
                  coproId={parseInt(coproId, 10)}
                  accentColor={company.brandingSettings?.color1 || '#134094'}
                  onSelect={(item) => {
                    setInitialValuation(item);
                    setValuationModalOpen(true);
                  }}
                />
              </div>
            )}

            <div className="mb-8">
              <h2 className="mb-4 text-2xl font-semibold">
                Collaborateurs attribués (
                {attributions.length}
                )
              </h2>
              {attributions.length > 0 ? (
                <MUITable
                  data={attributionData}
                  headers={attributionHeaders}
                  tableClass="w-full"
                  isSearchable
                  sortableColumns={['RÔLE', 'NOM', 'EMAIL']}
                  disablePagination
                />
              ) : (
                <div className="flex items-center justify-center">
                  <Badge
                    mode="warning"
                    text={(
                      <div className="text-center">
                        <span>Aucune attribution</span>
                        <br />
                        <Link
                          to={ROUTES.SETUP_ADD_COLLABORATORS}
                          className="underline hover:text-rgpd-blue-dark"
                        >
                          Ajouter des collaborateurs
                        </Link>
                      </div>
                    )}
                  />
                </div>
              )}
            </div>
            <div className="mb-8 grid grid-cols-1 lg:grid-cols-1">
              <h2 className="mb-6 text-2xl font-semibold">Historique dans le registre</h2>
              <CoproHistory coproId={copro.id} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-1">
              <h2 className="mb-6 text-2xl font-semibold">Localisation</h2>
              <div className="flex h-[55dvh] min-h-80 flex-col">
                <CoproDetailsMap parcelles={parcelles} addresses={addresses} />
              </div>
            </div>

          </>
        )}
      </div>

      {isSalesPopupOpen && (
        <Popup
          title="Ventes"
          onClose={() => setIsSalesPopupOpen(false)}
          width="800px"
          isOpen={isSalesPopupOpen}
        >
          <div className="p-4">
            <h2 className="mb-4 text-xl font-semibold">Ventes pour {copro.name}</h2>
            <p className="mb-4">
              Total: <span className="font-semibold">{formatCurrency(totalSales || 0)}</span> sur les 3 dernières années
            </p>
            {salesByYear.length > 0 && (
              <div className="mt-4 h-64">
                <LineChart
                  xAxis={[{ 
                    data: salesByYear.map(sale => new Date(sale.date)),
                    scaleType: 'time',
                    valueFormatter: (date) => formatDate(date.getTime()),
                  }]}
                  series={[
                    {
                      data: salesByYear.map(sale => sale.amount),
                      area: true,
                      showMark: true,
                      valueFormatter: (value) => formatCurrency(value),
                    },
                  ]}
                  height={250}
                />
              </div>
            )}
            <div className="mt-4">
              <MUITable
                headers={[
                  { title: 'Date' },
                  { title: 'Montant' },
                ]}
                data={sales.map((sale) => ({
                  Date: formatDate(sale.datemut),
                  Montant: formatCurrency(Number(sale.valeurfonc)),
                }))}
                tableClass="table-fixed"
              />
            </div>
          </div>
        </Popup>
      )}

      {isEditPopupOpen && (
        <Popup
          title="Modifier la copropriété"
          onClose={() => setIsEditPopupOpen(false)}
          width="800px"
          isOpen={isEditPopupOpen}
        >
          <div className="p-4">
            <CoproForm
              initialDataId={parseInt(coproId, 10)}
              setIsPopupOpen={setIsEditPopupOpen}
              companyId={company?.id}
            />
          </div>
        </Popup>
      )}

      {company && (
        <ValuationModal
          isOpen={valuationModalOpen}
          onClose={() => { setValuationModalOpen(false); setInitialValuation(undefined); }}
          onGenerated={() => valuationHistoryRef.current?.refresh()}
          coproId={parseInt(coproId, 10)}
          coproPostalCode={(addresses as Array<{ postalCode?: string }>)?.[0]?.postalCode ?? ''}
          coproCity={(addresses as Array<{ city?: string }>)?.[0]?.city ?? ''}
          coproAddress={coproAddress || ''}
          initialData={initialValuation}
        />
      )}
    </div>
  );
};

export default CoproID;
